
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../home/components/Footer';

export default function GlennsDonutsCaseStudy() {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Set page title
    document.title = "Glenn's Donuts Case Study | Donut Shop Website Design & Photography | Bellport Branding";

    // Helper to create / update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag(
      'description',
      "See how Bellport Branding designed and developed the website and provided professional photography for Glenn's Donuts in Selden, NY — a beloved Long Island donut shop serving fresh treats daily."
    );
    updateMetaTag(
      'keywords',
      "Glenn's Donuts case study, donut shop website design, Selden NY web design, bakery website, food photography Long Island"
    );

    // Open Graph meta tags
    updateMetaTag('og:type', 'article', true);
    updateMetaTag('og:title', "Glenn's Donuts Case Study | Website Design & Photography", true);
    updateMetaTag(
      'og:description',
      'Website design, development, and photography for Glenn\'s Donuts — a fresh donut shop in Selden, NY.',
      true
    );

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const siteUrl = (import.meta?.env?.VITE_SITE_URL as string) || 'https://www.bellportbranding.com';
    canonical.href = `${siteUrl}/portfolio/glenns-donuts`;

    // JSON‑LD article schema
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: "Glenn's Donuts Case Study - Donut Shop Website Design & Photography",
      description:
        "Website design, development, and professional photography for Glenn's Donuts, a beloved donut shop in Selden, NY serving fresh treats from Long Island's Glenn Wayne Bakery.",
      author: {
        '@type': 'Organization',
        name: 'Bellport Branding',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Bellport Branding',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`,
        },
      },
      datePublished: '2024-01-20',
      dateModified: '2024-01-20',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/portfolio/glenns-donuts`,
      },
    };

    // Insert / update the script tag that holds the schema
    let script = document.getElementById('glenns-article-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'glenns-article-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(articleSchema);

    // Cleanup on unmount
    return () => {
      const s = document.getElementById('glenns-article-schema');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl shadow-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/d7da4bfde00f775727e3be13864eec4a.png"
                alt="Bellport Branding"
                className="h-8 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <a
                href="tel:+16316407888"
                className="hidden md:flex items-center gap-2 text-neutral-900 hover:text-[#1E3A8A] transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-full bg-[#1E3A8A] flex items-center justify-center">
                  <i className="ri-phone-fill text-white text-sm"></i>
                </span>
                <span className="font-semibold">(631) 640-7888</span>
              </a>
              <Link
                to="/"
                className="bg-neutral-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs px-4 py-2 rounded-full font-medium mb-6">
                Website Design · Development · Photography
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-tight tracking-tight mb-6">
                Glenn&apos;s Donuts
              </h1>
              <p className="text-lg text-neutral-600 leading-relaxed mb-8">
                A warm, inviting website for a beloved Selden donut shop delivering fresh treats every morning from Long Island&apos;s Glenn
                Wayne Bakery. We handled the full website design, development, and product photography.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <a
                  href="https://www.glennsdonuts.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-neutral-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  Visit Live Site
                  <i className="ri-external-link-line"></i>
                </a>
                <Link
                  to="/#start-project"
                  className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Start Your Project
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-3xl font-semibold text-neutral-900">2024</p>
                  <p className="text-sm text-neutral-500">Year Completed</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-neutral-900">3</p>
                  <p className="text-sm text-neutral-500">Services Delivered</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-neutral-900">100%</p>
                  <p className="text-sm text-neutral-500">Fresh Daily</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/822cebb235da935b846eb465f1280cd5.jpg"
                  alt="Glenn's Donuts Website Design by Bellport Branding"
                  title="Glenn's Donuts Selden NY Website Design"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <i className="ri-camera-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">Pro Photography</p>
                    <p className="text-xs text-neutral-500">Product & Storefront Shots</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-semibold text-neutral-900 mb-6">Project Overview</h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Glenn&apos;s Donuts is a neighborhood favorite in Selden, New York, known for delivering the freshest donuts on Long Island.
                Their products arrive fresh every morning from Long Island&apos;s Glenn Wayne Bakery, crafted with love from the finest
                ingredients. Beyond donuts, they offer a wide variety of cakes, cookies, danishes, and muffins — all made to the highest
                standards.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-6">
                What sets Glenn&apos;s Donuts apart is their commitment to freshness — no donuts are kept overnight. Their popular &quot;Grab
                and Go&quot; freshly boxed assortments make it easy for customers to enjoy their most beloved flavors. They also offer
                delicious gluten‑free options including cupcakes, cookies, brownies, and more, served Thursday through Saturday.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                Bellport Branding provided a complete digital package: <strong>website design and development</strong> to showcase their
                menu and brand personality, plus <strong>professional photography</strong> to capture the mouthwatering appeal of their
                products and the welcoming atmosphere of their shop. We also integrated their existing menu into the website for easy customer access. The result is a site that makes you want to visit before you even
                finish scrolling.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-neutral-900 mb-6">Project Details</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Client</p>
                  <p className="text-neutral-900 font-medium">Glenn&apos;s Donuts</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Industry</p>
                  <p className="text-neutral-900 font-medium">Food &amp; Bakery</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Location</p>
                  <p className="text-neutral-900 font-medium">Selden, New York</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Services Provided</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">
                      Web Design
                    </span>
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">
                      Development
                    </span>
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">
                      Photography
                    </span>
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">
                      Menu Integration
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Website</p>
                  <a
                    href="https://www.glennsdonuts.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1E3A8A] font-medium hover:underline cursor-pointer"
                  >
                    www.glennsdonuts.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">What We Delivered</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              A complete digital presence designed to drive foot traffic and showcase the freshness that makes Glenn&apos;s Donuts a
              local favorite.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                <i className="ri-layout-4-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Custom Website Design</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                A warm, appetizing design that captures the shop&apos;s personality and makes visitors crave fresh donuts the moment they
                land on the page.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                <i className="ri-camera-lens-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Professional Photography</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Mouthwatering product photography and storefront shots that bring the in‑store experience to life online and drive customers
                through the door.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Mobile Responsive</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Fully optimized for mobile so customers can quickly check the menu, find the location, and get directions while on the go.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                <i className="ri-file-download-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Menu Integration</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Seamlessly integrated their existing menu into the website so customers can browse the full selection and plan their visit ahead of time.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                <i className="ri-instagram-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Instagram Integration</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Social media feed integration to keep the website fresh with the latest posts and build a stronger community connection.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Location &amp; Hours</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Clear display of store address, hours of operation, and an embedded map making it effortless for new customers to find the
                shop.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Website Screenshots */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">Website Showcase</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              A closer look at the design and photography we created for Glenn&apos;s Donuts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Colorful%20assortment%20of%20fresh%20glazed%20donuts%20arranged%20beautifully%20on%20display%20including%20chocolate%20frosted%20sprinkle%20donuts%20jelly%20filled%20boston%20cream%20powdered%20sugar%20donuts%20warm%20bakery%20lighting%20appetizing%20food%20photography%20professional%20product%20shot%20clean%20white%20marble%20surface&width=600&height=450&seq=glenns-products-showcase-02&orientation=landscape"
                  alt="Glenn's Donuts Fresh Product Photography"
                  title="Fresh donuts photography Selden NY"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Product Photography</h3>
              <p className="text-sm text-neutral-500">Mouthwatering shots of fresh daily donuts</p>
            </div>

            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20donut%20shop%20storefront%20exterior%20with%20large%20glass%20windows%20warm%20interior%20lighting%20visible%20display%20cases%20of%20fresh%20pastries%20inviting%20entrance%20clean%20signage%20welcoming%20neighborhood%20bakery%20professional%20architectural%20photography%20daytime%20shot&width=600&height=450&seq=glenns-storefront-showcase-03&orientation=landscape"
                  alt="Glenn's Donuts Storefront in Selden NY"
                  title="Glenn's Donuts shop Selden New York"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Storefront Photography</h3>
              <p className="text-sm text-neutral-500">Capturing the welcoming shop atmosphere</p>
            </div>

            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Bakery%20website%20menu%20page%20design%20showing%20grid%20of%20donut%20varieties%20with%20prices%20and%20descriptions%20warm%20color%20scheme%20appetizing%20food%20images%20clean%20modern%20layout%20downloadable%20menu%20section%20gluten%20free%20options%20highlighted%20professional%20web%20design%20for%20food%20business&width=600&height=450&seq=glenns-menu-showcase-04&orientation=landscape"
                  alt="Glenn's Donuts Menu Page Design"
                  title="Donut shop menu website design"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Menu &amp; Gluten-Free Options</h3>
              <p className="text-sm text-neutral-500">Easy browsing of the full product selection</p>
            </div>

            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Bakery%20website%20mobile%20responsive%20design%20mockup%20showing%20smartphone%20view%20of%20donut%20shop%20website%20with%20location%20map%20hours%20of%20operation%20contact%20information%20appetizing%20food%20images%20clean%20navigation%20touch%20friendly%20buttons%20warm%20inviting%20color%20scheme&width=600&height=450&seq=glenns-mobile-showcase-05&orientation=landscape"
                  alt="Glenn's Donuts Mobile Website Design"
                  title="Mobile responsive donut shop website"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Mobile Experience</h3>
              <p className="text-sm text-neutral-500">Seamless browsing on any device</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-neutral-900 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
            </div>

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">Want a website like this?</h2>
              <p className="text-neutral-400 max-w-xl mx-auto mb-8">
                Let&apos;s create a website and visuals that bring your business to life and drive real customers through your door.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+16316407888"
                  className="inline-flex items-center justify-center gap-2 bg-white text-neutral-900 px-8 py-4 rounded-full text-sm font-medium hover:bg-neutral-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-amber-600"></i>
                  Call (631) 640-7888
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border border-neutral-700 text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  View More Projects
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
